import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, Users, Link as LinkIcon, FileText } from 'lucide-react';
import { searchAPI } from '../lib/api';

var ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
var isMac = /Mac|iPhone|iPad|iPod/i.test(ua) && !/Windows/i.test(ua);
var modKeyName = isMac ? '⌘K' : 'Strg+K';

var ROLE_LABELS = {
  admin: 'Administrator', backoffice: 'Backoffice', personalmanager: 'Personalmanager',
  koordinator: 'Koordinator', geschaeftsfuehrung: 'Geschäftsführung', mitarbeiter: 'Mitarbeiter',
};
var STATUS_STYLES = {
  offen: 'bg-amber-100 text-amber-700',
  eingereicht: 'bg-blue-100 text-blue-700',
  genehmigt: 'bg-green-100 text-green-700',
  abgelehnt: 'bg-red-100 text-red-700',
};

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function Highlight({ text, term }) {
  if (!text || !term) return text || '';
  var toks = term.trim().split(/\s+/).filter(Boolean);
  if (!toks.length) return text || '';
  var tokenSet = {};
  for (var i = 0; i < toks.length; i++) tokenSet[toks[i].toLowerCase()] = true;
  var rx = new RegExp('(' + toks.map(escapeRegex).join('|') + ')', 'gi');
  var parts = String(text).split(rx);
  var elements = [];
  for (var j = 0; j < parts.length; j++) {
    var p = parts[j];
    if (p && tokenSet[p.toLowerCase()]) {
      elements.push(<mark key={j} className="bg-yellow-200 text-yellow-900 font-semibold rounded px-0.5">{p}</mark>);
    } else {
      elements.push(<React.Fragment key={j}>{p}</React.Fragment>);
    }
  }
  return <>{elements}</>;
}

function EmployeeRow({ e, active, onHover, onClick, query }) {
  return (
    <button data-active={active}
      onMouseEnter={onHover} onClick={onClick}
      className={'w-full px-4 py-3 border-b border-slate-100 text-left flex items-center gap-3 ' + (active ? 'bg-blue-50 ring-inset ring-2 ring-blue-400' : 'hover:bg-blue-50/60')}
      data-testid={'global-search-employee-' + e.id}>
      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
        <Users className="w-4 h-4 text-blue-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate flex items-center gap-2">
          <Highlight text={e.name} term={query} />
          {e.is_active === false && (
            <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Archiviert</span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate"><Highlight text={e.email} term={query} /></div>
      </div>
      <span className="text-xs text-slate-400 flex-shrink-0">{ROLE_LABELS[e.role] || e.role}</span>
    </button>
  );
}

function AssignmentRow({ a, active, onHover, onClick, query }) {
  return (
    <button data-active={active}
      onMouseEnter={onHover} onClick={onClick}
      className={'w-full px-4 py-3 border-b border-slate-100 text-left flex items-center gap-3 ' + (active ? 'bg-violet-50 ring-inset ring-2 ring-violet-400' : 'hover:bg-violet-50/60')}
      data-testid={'global-search-assignment-' + a.id}>
      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
        <LinkIcon className="w-4 h-4 text-violet-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate"><Highlight text={a.child_name} term={query} /></div>
        <div className="text-xs text-slate-500 truncate">
          <Highlight text={a.school_name} term={query} />
          {a.employee_name && <> · <Highlight text={a.employee_name} term={query} /></>}
        </div>
      </div>
      <span className="text-xs text-slate-400 flex-shrink-0">{a.federal_state}</span>
    </button>
  );
}

function TimesheetRow({ t, active, onHover, onClick, query }) {
  var label = (t.month_name || '') + ' ' + (t.year || '');
  return (
    <button data-active={active}
      onMouseEnter={onHover} onClick={onClick}
      className={'w-full px-4 py-3 border-b border-slate-100 text-left flex items-center gap-3 ' + (active ? 'bg-emerald-50 ring-inset ring-2 ring-emerald-400' : 'hover:bg-emerald-50/60')}
      data-testid={'global-search-timesheet-' + t.id}>
      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
        <FileText className="w-4 h-4 text-emerald-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate">
          <Highlight text={label} term={query} />
          {t.employee_name && <> – <Highlight text={t.employee_name} term={query} /></>}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {t.child_name && <>Kind: <Highlight text={t.child_name} term={query} /></>}
          {t.school_name && <> · <Highlight text={t.school_name} term={query} /></>}
        </div>
      </div>
      {t.status && (
        <span className={'text-[11px] px-2 py-0.5 rounded-full flex-shrink-0 ' + (STATUS_STYLES[t.status] || 'bg-slate-100 text-slate-600')}>
          {t.status}
        </span>
      )}
    </button>
  );
}

export default function GlobalSearch() {
  var navigate = useNavigate();
  var [open, setOpen] = useState(false);
  var [query, setQuery] = useState('');
  var [results, setResults] = useState(null);
  var [loading, setLoading] = useState(false);
  var [activeIndex, setActiveIndex] = useState(0);
  var inputRef = useRef(null);
  var debounceRef = useRef(null);
  var resultsContainerRef = useRef(null);

  useEffect(function() {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(function() {
    if (open && inputRef.current) setTimeout(function() { if (inputRef.current) inputRef.current.focus(); }, 50);
    if (!open) { setQuery(''); setResults(null); }
  }, [open]);

  useEffect(function() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(function() {
      searchAPI.global(query.trim()).then(function(res) {
        setResults(res.data);
      }).catch(function() {
        setResults({ query: query.trim(), total: 0, results: { employees: [], assignments: [], timesheets: [] } });
      }).finally(function() {
        setLoading(false);
      });
    }, 150);
    return function() { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  useEffect(function() { setActiveIndex(0); }, [results]);

  useEffect(function() {
    var c = resultsContainerRef.current;
    if (!c) return;
    var el = c.querySelector('[data-active="true"]');
    if (!el) return;
    var STICKY = 36;
    var cr = c.getBoundingClientRect();
    var er = el.getBoundingClientRect();
    if (er.top < cr.top + STICKY) c.scrollTop -= (cr.top + STICKY - er.top);
    else if (er.bottom > cr.bottom) c.scrollTop += (er.bottom - cr.bottom);
  }, [activeIndex]);

  function close() { setOpen(false); setQuery(''); setResults(null); }

  function handleSelect(type, id, role) {
    if (type === 'employees') {
      if (role === 'mitarbeiter') {
        navigate('/admin/users?focus=' + id);
      } else {
        navigate('/admin/settings?focus=' + id);
      }
    } else if (type === 'assignments') {
      navigate('/admin/assignments?focus=' + id);
    } else if (type === 'timesheets') {
      navigate('/timesheet/' + id);
    }
    close();
  }

  if (!open) {
    return (
      <button onClick={function() { setOpen(true); }}
        title={'Globale Suche (' + modKeyName + ')'}
        data-testid="global-search-trigger"
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700 text-sm transition">
        <Search className="w-4 h-4" />
        <span className="max-md:hidden">Suchen…</span>
        <kbd className="max-md:hidden text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-1 border border-slate-200 font-sans">{modKeyName}</kbd>
      </button>
    );
  }

  var emps = results && results.results ? results.results.employees || [] : [];
  var asgs = results && results.results ? results.results.assignments || [] : [];
  var tss = results && results.results ? results.results.timesheets || [] : [];

  var flatItems = [];
  for (var ei = 0; ei < emps.length; ei++) flatItems.push({ type: 'employees', id: emps[ei].id, role: emps[ei].role });
  for (var ai = 0; ai < asgs.length; ai++) flatItems.push({ type: 'assignments', id: asgs[ai].id });
  for (var ti = 0; ti < tss.length; ti++) flatItems.push({ type: 'timesheets', id: tss[ti].id });

  function onKeyNav(e) {
    if (!flatItems.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(function(i) { return (i + 1) % flatItems.length; }); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(function(i) { return (i - 1 + flatItems.length) % flatItems.length; }); }
    else if (e.key === 'Enter') { e.preventDefault(); var it = flatItems[activeIndex]; if (it) handleSelect(it.type, it.id, it.role); }
  }

  function isActive(type, id) { var it = flatItems[activeIndex]; return it && it.type === type && it.id === id; }
  function idxOf(type, id) {
    for (var x = 0; x < flatItems.length; x++) { if (flatItems[x].type === type && flatItems[x].id === id) return x; }
    return -1;
  }

  var empRows = [];
  for (var a = 0; a < emps.length; a++) {
    (function(e) {
      empRows.push(<EmployeeRow key={e.id} e={e} active={isActive('employees', e.id)} query={query}
        onHover={function() { var i = idxOf('employees', e.id); if (i >= 0) setActiveIndex(i); }}
        onClick={function() { handleSelect('employees', e.id, e.role); }} />);
    })(emps[a]);
  }
  var asgRows = [];
  for (var b = 0; b < asgs.length; b++) {
    (function(asg) {
      asgRows.push(<AssignmentRow key={asg.id} a={asg} active={isActive('assignments', asg.id)} query={query}
        onHover={function() { var i = idxOf('assignments', asg.id); if (i >= 0) setActiveIndex(i); }}
        onClick={function() { handleSelect('assignments', asg.id); }} />);
    })(asgs[b]);
  }
  var tsRows = [];
  for (var c = 0; c < tss.length; c++) {
    (function(ts) {
      tsRows.push(<TimesheetRow key={ts.id} t={ts} active={isActive('timesheets', ts.id)} query={query}
        onHover={function() { var i = idxOf('timesheets', ts.id); if (i >= 0) setActiveIndex(i); }}
        onClick={function() { handleSelect('timesheets', ts.id); }} />);
    })(tss[c]);
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={close} data-testid="global-search-overlay">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-xl shadow-2xl overflow-hidden ring-1 ring-black/10" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center gap-3 p-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input ref={inputRef} type="search" name="global-search-query" autoComplete="off"
            data-lpignore="true" data-1p-ignore="true" data-form-type="other" spellCheck="false"
            value={query} onChange={function(e) { setQuery(e.target.value); }} onKeyDown={onKeyNav}
            placeholder="Mitarbeiter, Zuordnungen, Stundenzettel …"
            className="flex-1 outline-none text-lg bg-transparent text-slate-900 placeholder:text-slate-400"
            data-testid="global-search-input" />
          {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
          <button onClick={close} className="text-slate-400 hover:text-slate-600 flex-shrink-0" aria-label="Schließen" data-testid="global-search-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div ref={resultsContainerRef} className="max-h-[55vh] overflow-y-auto" data-testid="global-search-results">
          {!results && query.trim().length < 2 && (
            <div className="p-8 text-center text-slate-400 text-sm">Mindestens 2 Zeichen eingeben</div>
          )}
          {results && results.total === 0 && (
            <div className="p-8 text-center text-slate-400" data-testid="global-search-empty">Keine Treffer für „{query}"</div>
          )}
          {emps.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 flex items-center gap-2 sticky top-0">
                <Users className="w-3.5 h-3.5" /> MITARBEITER ({emps.length})
              </div>
              {empRows}
            </div>
          )}
          {asgs.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 flex items-center gap-2 sticky top-0">
                <LinkIcon className="w-3.5 h-3.5" /> ZUORDNUNGEN ({asgs.length})
              </div>
              {asgRows}
            </div>
          )}
          {tss.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 flex items-center gap-2 sticky top-0">
                <FileText className="w-3.5 h-3.5" /> STUNDENZETTEL ({tss.length})
              </div>
              {tsRows}
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex justify-between">
          <span>↑↓ navigieren · ↵ öffnen · Esc schließen</span>
          {results && <span>{results.total} Treffer</span>}
        </div>
      </div>
    </div>,
    document.body
  );
}
